var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { defineComponent as _defineComponent } from 'vue';
import { library } from "@fortawesome/fontawesome-svg-core";
import { faCheckSquare, faListAlt, faSquare } from "@fortawesome/free-regular-svg-icons";
import { faArrowDown, faColumns, faSignInAlt } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/vue-fontawesome";
import { useInfiniteScroll } from "@vueuse/core";
import { BAlert, BBadge, BButton, BButtonGroup, BListGroup, BListGroupItem, BOverlay } from "bootstrap-vue";
import { storeToRefs } from "pinia";
import { computed, onMounted, onUnmounted, ref, watch } from "vue";
import { useRouter } from "vue-router/composables";
import { userOwnsHistory } from "@/api";
import { useAnimationFrameResizeObserver } from "@/composables/sensors/animationFrameResizeObserver";
import { useAnimationFrameScroll } from "@/composables/sensors/animationFrameScroll";
import { useHistoryStore } from "@/stores/historyStore";
import { useUserStore } from "@/stores/userStore";
import localize from "@/utils/localization";
import { HistoriesFilters } from "./HistoriesFilters";
import TextSummary from "../Common/TextSummary.vue";
import Heading from "@/components/Common/Heading.vue";
import StatelessTags from "@/components/TagsMultiselect/StatelessTags.vue";
import ScrollToTopButton from "@/components/ToolsList/ScrollToTopButton.vue";
import UtcDate from "@/components/UtcDate.vue";
export default /*#__PURE__*/ _defineComponent({
    __name: 'HistoryScrollList',
    props: {
        multiple: { type: Boolean, default: false },
        selectedHistories: { default: () => [] },
        additionalOptions: { default: () => [] },
        showModal: { type: Boolean, default: false },
        inModal: { type: Boolean, default: false },
        filter: { default: "" },
        loading: { type: Boolean, default: false }
    },
    emits: ["selectHistory", "setFilter", "update:loading", "update:show-modal"],
    setup(__props, { emit }) {
        const props = __props;
        library.add(faColumns, faSignInAlt, faListAlt, faArrowDown, faCheckSquare, faSquare);
        const busy = ref(false);
        const showAdvanced = ref(false);
        const scrollableDiv = ref(null);
        const historyStore = useHistoryStore();
        const { currentHistoryId, histories, totalHistoryCount, pinnedHistories } = storeToRefs(historyStore);
        const { currentUser } = storeToRefs(useUserStore());
        const hasNoResults = computed(() => props.filter && filtered.value.length == 0);
        const validFilter = computed(() => props.filter && props.filter.length > 2);
        const allLoaded = computed(() => totalHistoryCount.value <= filtered.value.length);
        // check if we have scrolled to the top or bottom of the scrollable div
        const { arrived, scrollTop } = useAnimationFrameScroll(scrollableDiv);
        const isScrollable = ref(false);
        useAnimationFrameResizeObserver(scrollableDiv, ({ clientSize, scrollSize }) => {
            isScrollable.value = scrollSize.height >= clientSize.height + 1;
        });
        const scrolledTop = computed(() => !isScrollable.value || arrived.top);
        const scrolledBottom = computed(() => !isScrollable.value || arrived.bottom);
        onMounted(() => __awaiter(this, void 0, void 0, function* () {
            useInfiniteScroll(scrollableDiv.value, () => loadMore());
            // if mounted with a filter, load histories for filter
            if (props.filter !== "" && validFilter.value) {
                yield loadMore(true);
            }
        }));
        onUnmounted(() => {
            // Remove the infinite scrolling behavior
            useInfiniteScroll(scrollableDiv.value, () => { });
        });
        /** if screen size is as such that a scroller is not rendered,
         * we load enough histories so that a scroller is rendered
         */
        watch(() => isScrollable.value, (scrollable) => {
            if (!scrollable && !allLoaded.value) {
                loadMore();
            }
        });
        watch(() => props.filter, (newVal, oldVal) => __awaiter(this, void 0, void 0, function* () {
            if (newVal !== "" && validFilter.value && newVal !== oldVal) {
                yield loadMore(true);
            }
        }));
        watch(() => busy.value, (loading) => {
            emit("update:loading", loading);
        });
        /** `historyStore` histories for current user */
        const historiesProxy = ref([]);
        watch(() => histories.value, (h) => {
            historiesProxy.value = h.filter((h) => userOwnsHistory(currentUser.value, h));
        }, {
            immediate: true,
        });
        const filtered = computed(() => {
            let filteredHistories = [];
            if (!validFilter.value) {
                filteredHistories = historiesProxy.value;
            }
            else {
                const filters = HistoriesFilters.getFiltersForText(props.filter);
                filteredHistories = historiesProxy.value.filter((history) => {
                    if (!HistoriesFilters.testFilters(filters, history)) {
                        return false;
                    }
                    return true;
                });
            }
            return filteredHistories.sort((a, b) => {
                if (!isMultiviewPanel.value && a.id == currentHistoryId.value) {
                    return -1;
                }
                else if (!isMultiviewPanel.value && b.id == currentHistoryId.value) {
                    return 1;
                }
                else if (a.update_time < b.update_time) {
                    return 1;
                }
                else {
                    return -1;
                }
            });
        });
        /** is this the selector list for Multiview that shows up in the left panel */
        const isMultiviewPanel = computed(() => !props.inModal && props.multiple);
        function isActiveItem(history) {
            if (isMultiviewPanel.value) {
                return pinnedHistories.value.some((item) => item.id == history.id);
            }
            else {
                return props.selectedHistories.some((item) => item.id == history.id);
            }
        }
        function historyClicked(history) {
            emit("selectHistory", history);
            if (isMultiviewPanel.value) {
                if (pinnedHistories.value.some((item) => item.id == history.id)) {
                    historyStore.unpinHistories([history.id]);
                }
                else {
                    openInMulti(history);
                }
            }
        }
        function scrollToTop() {
            var _a;
            (_a = scrollableDiv.value) === null || _a === void 0 ? void 0 : _a.scrollTo({ top: 0, behavior: "smooth" });
        }
        const router = useRouter();
        function setCurrentHistory(history) {
            historyStore.setCurrentHistory(history.id);
            emit("update:show-modal", false);
        }
        function setCenterPanelHistory(history) {
            router.push(`/histories/view?id=${history.id}`);
            emit("update:show-modal", false);
        }
        function setFilterValue(newFilter, newValue) {
            emit("setFilter", newFilter, newValue);
        }
        function openInMulti(history) {
            router.push("/histories/view_multiple");
            historyStore.pinHistory(history.id);
            emit("update:show-modal", false);
        }
        /** Loads (paginates) for more histories
         * @param noScroll If true, we are not scrolling and will load _all_ items for current filter
         */
        function loadMore(noScroll = false) {
            return __awaiter(this, void 0, void 0, function* () {
                if (!busy.value && (noScroll || (!noScroll && !props.filter && !allLoaded.value))) {
                    busy.value = true;
                    const queryString = props.filter && HistoriesFilters.getQueryString(props.filter);
                    yield historyStore.loadHistories(true, queryString);
                    busy.value = false;
                }
            });
        }
        return { __sfc: true, props, emit, busy, showAdvanced, scrollableDiv, historyStore, currentHistoryId, histories, totalHistoryCount, pinnedHistories, currentUser, hasNoResults, validFilter, allLoaded, arrived, scrollTop, isScrollable, scrolledTop, scrolledBottom, historiesProxy, filtered, isMultiviewPanel, isActiveItem, historyClicked, scrollToTop, router, setCurrentHistory, setCenterPanelHistory, setFilterValue, openInMulti, loadMore, faCheckSquare, faListAlt, faSquare, faArrowDown, faColumns, faSignInAlt, FontAwesomeIcon, BAlert, BBadge, BButton, BButtonGroup, BListGroup, BListGroupItem, BOverlay, localize, TextSummary, Heading, StatelessTags, ScrollToTopButton, UtcDate };
    }
});
